import React from 'react';
import styled from 'styled-components';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { FaBriefcase, FaGraduationCap } from 'react-icons/fa';

const Section = styled.section`
  margin: 20px 0;
  padding-bottom: 0 !important;

  .vertical-timeline-element:last-child {
    margin-bottom: 0 !important;
  }

  .vertical-timeline::before {
    background: linear-gradient(180deg, var(--accent-cyan, #22d3ee) 0%, var(--accent-blue, #2563eb) 100%);
    width: 3px;
  }
`;

const SectionLabel = styled.div`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.9rem;
  color: var(--text-muted, #64748b);
  margin-bottom: 30px;
  padding-left: 5%;

  .prompt {
    color: var(--accent-cyan, #22d3ee);
    margin-right: 8px;
    font-weight: 600;
  }
`;

const RoleTitle = styled.h3`
  font-family: var(--font-sans, 'Inter', sans-serif);
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary, #e2e8f0);
  margin-bottom: 4px;
`;

const CompanyName = styled.h4`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.85rem;
  color: var(--accent-cyan, #22d3ee);
  margin-bottom: 4px;
  font-weight: 500;
`;

const Location = styled.p`
  font-size: 0.8rem;
  color: var(--text-muted, #64748b);
  margin-bottom: 10px;
`;

const Description = styled.ul`
  margin: 8px 0 0 0;
  padding-left: 16px;
  font-size: 0.82rem;
  color: var(--text-secondary, #94a3b8);
  line-height: 1.6;

  li {
    margin-bottom: 4px;
  }
`;

const contentStyle = {
  background: 'rgba(30, 41, 59, 0.8)',
  color: '#e2e8f0',
  border: '1px solid rgba(148, 163, 184, 0.1)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  borderRadius: '10px',
  padding: '20px 24px',
};

const arrowStyle = { borderRight: '10px solid rgba(30, 41, 59, 0.8)' };
const workIconStyle = { background: '#2563eb', color: '#fff', boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.2)' };
const eduIconStyle = { background: '#10b981', color: '#fff', boxShadow: '0 0 0 4px rgba(16, 185, 129, 0.2)' };

const ExperienceTimeline = () => (
  <Section id="experience">
    <SectionLabel>
      <span className="prompt">$</span> git log --oneline experience/
    </SectionLabel>
    <VerticalTimeline>
      <VerticalTimelineElement
        contentStyle={contentStyle}
        contentArrowStyle={arrowStyle}
        date="June 2025 – Present"
        iconStyle={workIconStyle}
        icon={<FaBriefcase />}
        position="right"
      >
        <RoleTitle>Full-Stack Developer</RoleTitle>
        <CompanyName>XOPS</CompanyName>
        <Location>Pleasanton, CA</Location>
        <Description>
          <li>Building scalable full-stack applications and internal tools</li>
        </Description>
      </VerticalTimelineElement>

      <VerticalTimelineElement
        contentStyle={contentStyle}
        contentArrowStyle={arrowStyle}
        date="October 2023 – May 2025"
        iconStyle={workIconStyle}
        icon={<FaBriefcase />}
        position="right"
      >
        <RoleTitle>Technical Assistant</RoleTitle>
        <CompanyName>USC Rossier School of Education</CompanyName>
        <Location>Los Angeles, CA</Location>
        <Description>
          <li>Provided technical support and developed tooling for faculty and research teams</li>
        </Description>
      </VerticalTimelineElement>

      <VerticalTimelineElement
        contentStyle={contentStyle}
        contentArrowStyle={arrowStyle}
        date="May 2024 – July 2024"
        iconStyle={workIconStyle}
        icon={<FaBriefcase />}
        position="right"
      >
        <RoleTitle>Software Engineer Intern</RoleTitle>
        <CompanyName>Live Risk Labs, LLC</CompanyName>
        <Location>New York, NY</Location>
        <Description>
          <li>Engineered a cybersecurity risk dashboard in React, visualizing 50+ critical security metrics</li>
        </Description>
      </VerticalTimelineElement>

      <VerticalTimelineElement
        contentStyle={contentStyle}
        contentArrowStyle={arrowStyle}
        date="August 2023 – May 2025"
        iconStyle={eduIconStyle}
        icon={<FaGraduationCap />}
        position="left"
      >
        <RoleTitle>Master of Science in Computer Science</RoleTitle>
        <CompanyName>University of Southern California</CompanyName>
        <Location>Los Angeles, CA</Location>
      </VerticalTimelineElement>

      <VerticalTimelineElement
        contentStyle={contentStyle}
        contentArrowStyle={arrowStyle}
        date="October 2022 – April 2023"
        iconStyle={workIconStyle}
        icon={<FaBriefcase />}
        position="right"
      >
        <RoleTitle>Project Developer</RoleTitle>
        <CompanyName>Vision Mechatronics Pvt. Ltd</CompanyName>
        <Location>Mumbai, India</Location>
        <Description>
          <li>Developed IoT data processing pipelines and monitoring dashboards</li>
        </Description>
      </VerticalTimelineElement>

      <VerticalTimelineElement
        contentStyle={contentStyle}
        contentArrowStyle={arrowStyle}
        date="June 2022 – August 2022"
        iconStyle={workIconStyle}
        icon={<FaBriefcase />}
        position="right"
      >
        <RoleTitle>Software Engineer Intern</RoleTitle>
        <CompanyName>ConnectWise</CompanyName>
        <Location>Mumbai, India</Location>
        <Description>
          <li>Enhanced system security and developed features for enterprise IT management platform</li>
        </Description>
      </VerticalTimelineElement>

      <VerticalTimelineElement
        contentStyle={contentStyle}
        contentArrowStyle={arrowStyle}
        date="May 2020 – July 2020"
        iconStyle={workIconStyle}
        icon={<FaBriefcase />}
        position="right"
      >
        <RoleTitle>Research Contributor Intern</RoleTitle>
        <CompanyName>Our Global Services, LLC</CompanyName>
        <Location>New York City, USA (Remote)</Location>
      </VerticalTimelineElement>

      <VerticalTimelineElement
        contentStyle={contentStyle}
        contentArrowStyle={arrowStyle}
        date="August 2019 – May 2023"
        iconStyle={eduIconStyle}
        icon={<FaGraduationCap />}
        position="left"
      >
        <RoleTitle>Bachelor of Engineering in Information Technology</RoleTitle>
        <CompanyName>Shah & Anchor Kutchhi Engineering College</CompanyName>
        <Location>Mumbai, India</Location>
      </VerticalTimelineElement>
    </VerticalTimeline>
  </Section>
);

export default ExperienceTimeline;
